import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import {
    // AutoComplete,
    Flex,
    Input,
    List
    // Tabs
} from "antd";
import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { getSourceEntities } from '../../api/template/template';
import { PATHS } from '../../constants/PATHS';
import { useUserData } from '../../hooks/useUserProfile';
import { useAppDispatch, useAppSelector } from '../../store/hooks/hooks';
import { setEntity } from '../../store/slices/dataverseSlice';
import { createErrorMessage } from '../../utils/createErrorMessage';
import { ErrorView } from "../Error/ErrorView";

export const EntitiesP = () => {
    const { instance } = useMsal();
    const account = instance.getActiveAccount();
    const { organizationId, tenantId } = useUserData();
    const selectedInstance = useAppSelector(state => state.dataverse.selectedInstance);
    const OperationIdentifier = useAppSelector(state => state.dataverse.operationIdentifier);

    const { data, error, isLoading, isError } = useQuery({
        queryKey: ['entities', selectedInstance, organizationId, tenantId, OperationIdentifier, account?.username],
        queryFn: async () => {
            try {
                const instanceToken = await instance.acquireTokenSilent({
                    account: account ?? undefined,
                    scopes: [`${selectedInstance}/.default`],
                });

                if (!instanceToken) {
                    throw new Error("No access token");
                }
                
                const res = await getSourceEntities({
                        AdapterIdentifier: "DataverseAdapter",
                        TenantId: tenantId,
                        OrganizationId: organizationId,
                        ImportConfigurationArguments: {
                            InstanceUrl: selectedInstance
                        },
                        OperationIdentifier
                    },
                    {
                        "X-MS-Access-Token": instanceToken?.accessToken
                    }
                );
                return res.data.data;
            } catch (error) {
            toast.error(createErrorMessage(error));
            }
        },
        enabled: !!selectedInstance && !!account && !!OperationIdentifier // Only run the query if we have an access token
    });

    if (isLoading) return <p>Loading...</p>;

    if (isError) return <ErrorView error={error} showDetails />;

    return <Entities dvEntities={data} />;
};

export const Entities = ({ dvEntities }: { dvEntities: any[] }) => {
    const urlParams = useParams();
    const navigate = useNavigate();
    const [searchString, setSearchString] = useState("");
    const selectedEntity = useAppSelector(state => state.dataverse.selectedEntity);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (urlParams.entityIdentifier) {
            const selectedEntity = dvEntities?.find(entity => entity.identifier === urlParams?.entityIdentifier);
            
            if (selectedEntity) {
                dispatch(setEntity(selectedEntity.identifier));
            }
        }
    }, [urlParams.entityIdentifier, dvEntities, dispatch]);

    const items = dvEntities?.map((entity) => ({
        label: entity.displayName || entity.identifier,
        value: entity.identifier,
    }));
    return (
        <Flex align="flex-start" justify="flex-start" gap={20} style={{ width: "100%" }}>
            <Flex vertical style={{ width: "100%", maxWidth: 200 }}>
                <Input.Search
                    placeholder="Search Entities"
                    value={searchString}
                    onChange={(e) => setSearchString(e.target.value)}
                    variant="outlined"
                    style={{ width: "100%", marginBottom: "16px" }}
                />
                <List
                    style={{ width: "100%", height: "100%", maxHeight: "400px", overflowY: "auto" }}
                    dataSource={items?.filter(item => item.label.toLowerCase().includes(searchString.toLowerCase()))}
                    renderItem={(item) => (
                        <List.Item
                            key={item.value}
                            onClick={() => {
                                navigate(`${PATHS.DATAVERSE}/${urlParams.destinationIdentifier}/${urlParams.instanceIdentifier}/${item.value}`);
                                dispatch(setEntity(item.value));
                            }}
                            style={{ cursor: "pointer", padding: "8px 16px" }}
                        >
                            <Flex justify="space-between" align="center" style={{ width: "100%" }}>
                                <span>{item.label}</span>
                                {selectedEntity === item.value && <span>Selected</span>}
                            </Flex>
                        </List.Item>
                    )}
                />
                {/* <AutoComplete
                    popupMatchSelectWidth
                    allowClear
                    // value={items.find((item) => item.value === selectedInstance)}
                    value={selectedEntity}
                    options={items}
                    placeholder="Select a Dataverse Entity"
                    filterOption={(inputValue, option) => {
                        const regex = new RegExp(inputValue, "i");
                        return regex.test(option?.value ?? "");
                    }}
                    onSelect={(value, option) => {
                        navigate(`${PATHS.DATAVERSE}/${urlParams.destinationIdentifier}/${urlParams.instanceIdentifier}/${value}`);
                        dispatch(setEntity(value));
                    }}
                    onClear={() => {
                        navigate(`${PATHS.DATAVERSE}/${urlParams.destinationIdentifier}/${urlParams.instanceIdentifier}`);
                        dispatch(setEntity(""));
                    }}
                    style={{ width: "150px" }}
                /> */}
                
            </Flex>
            <Outlet />
        </Flex>
    );
};
